import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementLocale } from '@stripe/stripe-js';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, theme } from '@squire/ui';

import { BottomSheet } from '@/components/_shared/BottomSheet';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet/BottomSheet';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { sanitizeBnplCurrency } from '@/utils/bnpl';
import { getStripePromise } from '@/utils/stripeLoader';

import { BnplForm } from './BnplForm';
import type { BnplBottomSheetProps } from './types';

const BnplBottomSheet = forwardRef<BottomSheetRef, BnplBottomSheetProps>(
  (props, ref) => {
    const { i18n } = useTranslation();
    const { t } = useScreenTranslations('BnplBottomSheet');
    const internalRef = useRef<BottomSheetRef>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [stripePromise] = useState(getStripePromise);

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsInitialized(true);
        internalRef.current?.open();
      },
      close: () => internalRef.current?.close(),
      snapTo: (index) => internalRef.current?.snapTo(index),
      snapToIndex: (index) => internalRef.current?.snapToIndex(index),
    }));

    // Server-side payment session is the source of truth for BNPL
    // eligibility (it gates the payment method selector). Stripe requires
    // amount > 0 in payment mode, so clamp like water-on-web does.
    const amount = Math.max(
      1,
      Math.round(props.cart.breakdown?.outstandingChargeAmount ?? 0),
    );

    return (
      <BottomSheet
        accessibilityLabel={t('title')}
        ref={internalRef}
        hideHandle
        scrollable
        // Covers every dismissal path (X close, backdrop press, drag-down).
        // The success path is a full-page redirect to the BNPL provider, so it
        // never dismisses this sheet — a dismissal here is always the user
        // backing out, and the parent must release the Book button's loading
        // state (SQR-15310).
        onDismiss={props.onCancel}
      >
        {isInitialized && (
          <>
            <Box padding={theme.spacing[6]}>
              <ScreenHeader
                title={t('title')}
                onClose={() => internalRef.current?.close()}
              />
            </Box>
            <Elements
              stripe={stripePromise}
              options={{
                mode: 'payment',
                currency: sanitizeBnplCurrency(props.shop.brand?.currency),
                amount,
                paymentMethodCreation: 'manual',
                paymentMethodTypes: props.eligibleMethods,
                locale: i18n.language as StripeElementLocale,
              }}
            >
              <BnplForm
                cart={props.cart}
                shop={props.shop}
                eligibleMethods={props.eligibleMethods}
                customer={props.customer}
                addPaymentAndCustomer={props.addPaymentAndCustomer!}
                onError={props.onError}
              />
            </Elements>
          </>
        )}
      </BottomSheet>
    );
  },
);

BnplBottomSheet.displayName = 'BnplBottomSheet';
export { BnplBottomSheet };
